import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // === MAIN GAME ANIMATION STATES ===
  counterAnimationStarted: false, // Tracks if the counter animation is started
  counterAnimationFinished: false, // Tracks if the counter animation is finished
  skipCounterAnimation: false, // Tracks if user wants to skip counter animation immediately
  linesRotationFinished: false, // Tracks if the lines rotation is finished
  currentWinningLine: null, // Tracks the current winning line
  winAnimationsPlaying: false, // Tracks if win animations (spine + counter) are playing
  criticalAnimationPlaying: false, // Universal blocker - blocks user input during critical animations (expanding wilds, intro sequences, etc.)
  userWantsToDismissWinAnimation: false, // Tracks if user wants to dismiss win animation
  skipWinAnimation: false, // Tracks if user wants to skip win animation immediately
  isWaitingForUserConfirmation: false, // Tracks if animation is waiting for user to dismiss
  animatedWinAmount: 0, // Current animated win amount value (persists across screen resizes)
  resetMainGameVisualEffects: false, // Trigger to reset main game visual effects and animations
  introTransitionActive: false, // Tracks if intro popup transition is active
  showGameAfterTransition: false, // Tracks if game should be shown after transition completes
  showHoldAndWinIntro: false, // Tracks if hold and win intro dialog should be shown
  sceneTransitionActive: false, // Tracks if scene transition overlay is active
  showHoldAndWinOutro: false, // Tracks if hold and win outro dialog should be shown
  showHoldAndWinCollector: false, // Tracks if hold and win collector dialog should be shown
  showMaxWin: false, // Tracks if max win dialog should be shown
  expandingWildsOccurred: false, // Tracks if expanding wilds occurred in the current spin
};

const animationsSlice = createSlice({
  name: "animations",
  initialState,
  reducers: {
    // === MAIN GAME ANIMATION REDUCERS ===
    setCounterAnimationStarted: (state, action) => {
      state.counterAnimationStarted = action.payload;
    },

    setCounterAnimationFinished: (state, action) => {
      state.counterAnimationFinished = action.payload;
    },

    setSkipCounterAnimation: (state, action) => {
      state.skipCounterAnimation = action.payload;
    },

    setCurrentWinningLine: (state, action) => {
      state.currentWinningLine = action.payload;
    },

    setWinAnimationsPlaying: (state, action) => {
      state.winAnimationsPlaying = action.payload;
    },

    setCriticalAnimationPlaying: (state, action) => {
      state.criticalAnimationPlaying = action.payload;
    },

    setUserWantsToDismissWinAnimation: (state, action) => {
      state.userWantsToDismissWinAnimation = action.payload;
    },

    setSkipWinAnimation: (state, action) => {
      state.skipWinAnimation = action.payload;
    },

    setIsWaitingForUserConfirmation: (state, action) => {
      state.isWaitingForUserConfirmation = action.payload;
    },

    // Update animated win amount value (for persistence across screen resizes)
    updateAnimatedWinAmount: (state, action) => {
      state.animatedWinAmount = action.payload;
    },

    // Reset main game visual effects and animations (triggered during free spin transition)
    setResetMainGameVisualEffects: (state, action) => {
      state.resetMainGameVisualEffects = action.payload;
    },

    // Control intro transition state
    setIntroTransitionActive: (state, action) => {
      state.introTransitionActive = action.payload;
    },

    // Control whether game should be shown after transition
    setShowGameAfterTransition: (state, action) => {
      state.showGameAfterTransition = action.payload;
    },

    // Control hold and win intro dialog visibility
    setShowHoldAndWinIntro: (state, action) => {
      state.showHoldAndWinIntro = action.payload;
    },

    // Control scene transition overlay visibility
    setSceneTransitionActive: (state, action) => {
      state.sceneTransitionActive = action.payload;
    },

    // Control hold and win outro dialog visibility
    setShowHoldAndWinOutro: (state, action) => {
      state.showHoldAndWinOutro = action.payload;
    },

    // Control hold and win collector dialog visibility
    setShowHoldAndWinCollector: (state, action) => {
      state.showHoldAndWinCollector = action.payload;
    },

    // Control max win dialog visibility
    setShowMaxWin: (state, action) => {
      state.showMaxWin = action.payload;
    },

    // Track if expanding wilds occurred in current spin
    setExpandingWildsOccurred: (state, action) => {
      state.expandingWildsOccurred = action.payload;
    },
  },
});

export const selectIntroTransitionActive = (state) =>
  state.animations.introTransitionActive;

export const selectShowGameAfterTransition = (state) =>
  state.animations.showGameAfterTransition;

export const selectResetMainGameVisualEffects = (state) =>
  state.animations.resetMainGameVisualEffects;

export const selectCounterAnimationFinished = (state) =>
  state.animations.counterAnimationFinished;

export const selectLinesRotationFinished = (state) =>
  state.animations.linesRotationFinished;

export const selectWinAnimationsPlaying = (state) =>
  state.animations.winAnimationsPlaying;

export const selectCriticalAnimationPlaying = (state) =>
  state.animations.criticalAnimationPlaying;

export const selectUserWantsToDismissWinAnimation = (state) =>
  state.animations.userWantsToDismissWinAnimation;

export const selectSkipWinAnimation = (state) =>
  state.animations.skipWinAnimation;

export const selectIsWaitingForUserConfirmation = (state) =>
  state.animations.isWaitingForUserConfirmation;

export const selectAnimatedWinAmount = (state) =>
  state.animations.animatedWinAmount;

export const selectCounterAnimationStarted = (state) =>
  state.animations.counterAnimationStarted;

export const selectSkipCounterAnimation = (state) =>
  state.animations.skipCounterAnimation;

export const selectShowHoldAndWinIntro = (state) =>
  state.animations.showHoldAndWinIntro;

export const selectSceneTransitionActive = (state) =>
  state.animations.sceneTransitionActive;

export const selectShowHoldAndWinOutro = (state) =>
  state.animations.showHoldAndWinOutro;

export const selectShowHoldAndWinCollector = (state) =>
  state.animations.showHoldAndWinCollector;

export const selectShowMaxWin = (state) => state.animations.showMaxWin;

export const selectExpandingWildsOccurred = (state) =>
  state.animations.expandingWildsOccurred;

export const {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setSkipCounterAnimation,
  setCurrentWinningLine,
  setWinAnimationsPlaying,
  setCriticalAnimationPlaying,
  setUserWantsToDismissWinAnimation,
  setSkipWinAnimation,
  setIsWaitingForUserConfirmation,
  updateAnimatedWinAmount,
  setResetMainGameVisualEffects,
  setIntroTransitionActive,
  setShowGameAfterTransition,
  setShowHoldAndWinIntro,
  setSceneTransitionActive,
  setShowHoldAndWinOutro,
  setShowHoldAndWinCollector,
  setShowMaxWin,
  setExpandingWildsOccurred,
} = animationsSlice.actions;

export default animationsSlice.reducer;
